import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { 
  ArrowLeft, 
  Calendar as CalendarIcon,
  Home,
  Target,
  Plus,
  Edit,
  Trash2,
  MapPin,
  Clock,
  ChevronLeft,
  ChevronRight,
  Shuffle,
  Settings
} from "lucide-react";

const EVENT_TYPES = [
  { value: "treino", label: "Treino", color: "bg-blue-500" },
  { value: "jogo", label: "Jogo", color: "bg-green-500" },
  { value: "outro", label: "Outro", color: "bg-purple-500" },
];

const MONTHS = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

export default function CalendarPage({ evaluator }) {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [gamedays, setGamedays] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [locationsDialogOpen, setLocationsDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [newEvent, setNewEvent] = useState({
    title: "",
    event_type: "treino",
    date: "",
    time: "",
    location: "",
    description: "",
    gameday_id: ""
  });
  const [newLocation, setNewLocation] = useState({ name: "", address: "" });

  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  const fetchData = async () => {
    try {
      const [eventsRes, gamedaysRes, locationsRes] = await Promise.all([
        axios.get(`${API}/calendar?month=${(currentMonth + 1).toString()}&year=${currentYear}`),
        axios.get(`${API}/gamedays`),
        axios.get(`${API}/locations`).catch(() => ({ data: [] }))
      ]);
      setEvents(eventsRes.data);
      setGamedays(gamedaysRes.data);
      setLocations(locationsRes.data || []);
    } catch (error) {
      toast.error("Erro ao carregar eventos");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth, currentYear]);

  const handleCreateLocation = async () => {
    if (!newLocation.name.trim()) {
      toast.error("Digite o nome do local");
      return;
    }
    try {
      await axios.post(`${API}/locations`, newLocation);
      toast.success("Local cadastrado com sucesso!");
      setNewLocation({ name: "", address: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao cadastrar local");
    }
  };

  const handleDeleteLocation = async (locationId) => {
    try {
      await axios.delete(`${API}/locations/${locationId}`);
      toast.success("Local removido!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao remover local");
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    if (!newEvent.title || !newEvent.date) {
      toast.error("Preencha título e data");
      return;
    }

    try {
      if (editingEvent) {
        await axios.put(`${API}/calendar/${editingEvent.id}`, newEvent);
        toast.success("Evento atualizado!");
      } else {
        await axios.post(`${API}/calendar`, newEvent);
        toast.success("Evento criado!");
      }
      setDialogOpen(false);
      setEditingEvent(null);
      setNewEvent({ title: "", event_type: "treino", date: "", time: "", location: "", description: "", gameday_id: "" });
      fetchData();
    } catch (error) {
      toast.error("Erro ao salvar evento");
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Deseja excluir este evento?")) return;
    try {
      await axios.delete(`${API}/calendar/${eventId}`);
      toast.success("Evento excluído!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao excluir evento");
    }
  };

  const openEditDialog = (event) => {
    setEditingEvent(event);
    setNewEvent({
      title: event.title,
      event_type: event.event_type,
      date: event.date,
      time: event.time || "",
      location: event.location || "",
      description: event.description || "",
      gameday_id: event.gameday_id || ""
    });
    setDialogOpen(true);
  };

  const openCreateDialog = (date = "") => {
    setEditingEvent(null);
    setNewEvent({ title: "", event_type: "treino", date, time: "", location: "", description: "", gameday_id: "" });
    setDialogOpen(true);
  };

  // Calendar generation
  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const daysInMonth = getDaysInMonth(currentMonth, currentYear);
  const firstDay = getFirstDayOfMonth(currentMonth, currentYear);

  const calendarDays = [];
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    calendarDays.push(i);
  }

  const getEventsForDay = (day) => {
    const dateStr = `${currentYear}-${(currentMonth + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    return events.filter(e => e.date === dateStr);
  };

  const getEventTypeColor = (type) => {
    return EVENT_TYPES.find(t => t.value === type)?.color || "bg-gray-500";
  };

  const prevMonth = () => setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(currentYear, currentMonth + 1, 1));

  const canEdit = evaluator?.role === 'admin' || evaluator?.role === 'tecnico';

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-btn">
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl md:text-2xl font-bold tracking-tight">Calendário</h1>
                <p className="text-sm text-muted-foreground">Jogos e treinos programados</p>
              </div>
            </div>
            {canEdit && (
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => setLocationsDialogOpen(true)} data-testid="manage-locations-btn">
                  <MapPin className="mr-2 h-4 w-4" />
                  Locais
                </Button>
                <Button onClick={() => openCreateDialog()} data-testid="new-event-btn">
                  <Plus className="mr-2 h-4 w-4" />
                  Novo Evento
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 content-with-nav">
        <Card className="bg-card border-border/50 shadow-xl">
          <CardHeader>
            <div className="flex items-center justify-between">
              <Button variant="ghost" size="icon" onClick={prevMonth}>
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <CardTitle className="text-xl font-bold">
                {MONTHS[currentMonth]} {currentYear}
              </CardTitle>
              <Button variant="ghost" size="icon" onClick={nextMonth}>
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Carregando...</div>
            ) : (
              <div className="grid grid-cols-7 gap-1">
                {/* Days of week header */}
                {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map(day => (
                  <div key={day} className="text-center text-xs font-bold text-muted-foreground py-2">
                    {day}
                  </div>
                ))}
                {/* Calendar days */}
                {calendarDays.map((day, index) => {
                  const dayEvents = day ? getEventsForDay(day) : [];
                  const isToday = day === new Date().getDate() && 
                    currentMonth === new Date().getMonth() && 
                    currentYear === new Date().getFullYear();
                  
                  return (
                    <div
                      key={day ? `day-${day}` : `empty-${index}`}
                      className={`min-h-[100px] p-1 border border-border/30 rounded-lg ${
                        day ? 'hover:bg-secondary/30 cursor-pointer' : 'bg-secondary/10'
                      } ${isToday ? 'ring-2 ring-primary' : ''}`}
                      onClick={() => day && canEdit && openCreateDialog(`${currentYear}-${(currentMonth + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`)}
                    >
                      {day && (
                        <>
                          <div className={`text-sm font-medium ${isToday ? 'text-primary' : ''}`}>{day}</div>
                          <div className="space-y-1 mt-1">
                            {dayEvents.slice(0, 3).map(event => (
                              <div
                                key={event.id}
                                className={`${getEventTypeColor(event.event_type)} text-white text-xs px-1 py-0.5 rounded truncate`}
                                onClick={(e) => { e.stopPropagation(); openEditDialog(event); }}
                                title={event.title}
                              >
                                {event.title}
                              </div>
                            ))}
                            {dayEvents.length > 3 && (
                              <div className="text-xs text-muted-foreground">+{dayEvents.length - 3} mais</div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Events */}
        <Card className="mt-6 bg-card border-border/50 shadow-xl">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-primary" />
              Próximos Eventos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {events.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">Nenhum evento este mês</p>
              ) : (
                events.sort((a, b) => a.date.localeCompare(b.date)).map(event => (
                  <div key={event.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${getEventTypeColor(event.event_type)}`} />
                      <div>
                        <p className="font-medium">{event.title}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{event.date}</span>
                          {event.time && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{event.time}</span>}
                          {event.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{event.location}</span>}
                        </div>
                      </div>
                    </div>
                    {canEdit && (
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEditDialog(event)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDeleteEvent(event.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Event Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>{editingEvent ? "Editar Evento" : "Novo Evento"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateEvent} className="space-y-4">
            <div className="space-y-2">
              <Label>Título *</Label>
              <Input
                value={newEvent.title}
                onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                placeholder="Ex: Treino Tático"
                className="bg-input/50"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={newEvent.event_type} onValueChange={(v) => setNewEvent({ ...newEvent, event_type: v })}>
                  <SelectTrigger className="bg-input/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EVENT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Data *</Label>
                <Input
                  type="date"
                  value={newEvent.date}
                  onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                  className="bg-input/50"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Horário</Label>
                <Input
                  type="time"
                  value={newEvent.time}
                  onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })}
                  className="bg-input/50"
                />
              </div>
              <div className="space-y-2">
                <Label>Local</Label>
                <Select 
                  value={newEvent.location || "custom"} 
                  onValueChange={(v) => setNewEvent({ ...newEvent, location: v === "custom" ? "" : v })}
                >
                  <SelectTrigger className="bg-input/50">
                    <SelectValue placeholder="Selecione ou digite" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">Digitar manualmente...</SelectItem>
                    {locations.map(loc => (
                      <SelectItem key={loc.id} value={loc.name}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(newEvent.location === "" || !locations.find(l => l.name === newEvent.location)) && (
                  <Input
                    value={newEvent.location}
                    onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                    placeholder="Ex: Ginásio Central"
                    className="bg-input/50 mt-2"
                  />
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Vincular ao Times do Dia</Label>
              <Select value={newEvent.gameday_id || "none"} onValueChange={(v) => setNewEvent({ ...newEvent, gameday_id: v === "none" ? "" : v })}>
                <SelectTrigger className="bg-input/50">
                  <SelectValue placeholder="Nenhum" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum</SelectItem>
                  {gamedays.map(gd => (
                    <SelectItem key={gd.id} value={gd.id}>Times do Dia - {gd.date}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Descrição</Label>
              <Textarea
                value={newEvent.description}
                onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                placeholder="Detalhes do evento..."
                className="bg-input/50"
              />
            </div>
            <Button type="submit" className="w-full bg-primary">
              {editingEvent ? "Salvar" : "Criar Evento"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Locations Management Dialog */}
      <Dialog open={locationsDialogOpen} onOpenChange={setLocationsDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-primary" />
              Gerenciar Locais
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Add new location */}
            <div className="space-y-3 p-3 rounded-lg bg-secondary/30">
              <Label>Adicionar Novo Local</Label>
              <Input
                value={newLocation.name}
                onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                placeholder="Nome do local"
                className="bg-input/50"
              />
              <Input
                value={newLocation.address}
                onChange={(e) => setNewLocation({ ...newLocation, address: e.target.value })}
                placeholder="Endereço (opcional)"
                className="bg-input/50"
              />
              <Button onClick={handleCreateLocation} className="w-full">
                <Plus className="mr-2 h-4 w-4" />
                Adicionar Local
              </Button>
            </div>

            {/* List of locations */}
            <div className="space-y-2">
              <Label>Locais Cadastrados</Label>
              {locations.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Nenhum local cadastrado
                </p>
              ) : (
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {locations.map(loc => (
                    <div key={loc.id} className="flex items-center justify-between p-2 rounded-lg bg-secondary/20">
                      <div>
                        <p className="font-medium text-sm">{loc.name}</p>
                        {loc.address && (
                          <p className="text-xs text-muted-foreground">{loc.address}</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleDeleteLocation(loc.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mobile Navigation */}
      <nav className="mobile-nav">
        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="flex flex-col items-center gap-1 h-auto py-2">
          <Home className="h-5 w-5" />
          <span className="text-xs">Início</span>
        </Button>
        <Button variant="ghost" size="sm" onClick={() => navigate('/calendar')} className="flex flex-col items-center gap-1 h-auto py-2 text-primary">
          <CalendarIcon className="h-5 w-5" />
          <span className="text-xs">Calendário</span>
        </Button>
        <Button variant="ghost" size="sm" onClick={() => navigate('/gameday')} className="flex flex-col items-center gap-1 h-auto py-2">
          <Shuffle className="h-5 w-5" />
          <span className="text-xs">Times</span>
        </Button>
        <Button variant="ghost" size="sm" onClick={() => navigate('/evaluate')} className="flex flex-col items-center gap-1 h-auto py-2">
          <Target className="h-5 w-5" />
          <span className="text-xs">Avaliar</span>
        </Button>
      </nav>
    </div>
  );
}
